import { useCallback, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { Box } from '@factorialco/deprecated-design-system'
import { TwoColumnLayout } from '@factorialco/f0-react'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { WorkSpacesPageDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useQuery } from 'modules/core/hooks/useQuery'
import { useStrictParams } from 'modules/core/hooks/useStrictParams'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import FileUploaderModal from 'modules/locations/components/WorkAreas/FileUploaderModal'
import LocationWorkSpaces from 'modules/locations/components/WorkSpaces'
import { AlertMessage } from 'modules/locations/components/WorkSpaces/AlertMessage'
import { PinAlert } from 'modules/locations/components/WorkSpaces/PinAlert'
import { WorkSpaceContext } from 'modules/locations/contexts/workSpaces/WorkSpacesContext'
import { useWorkAreaMapAvailability } from 'modules/locations/hooks/useWorkAreaMapAvailability'
import { WorkSpacesMap } from 'modules/space_control/components/WorkSpacesMap'
import { MAP_MODE } from 'modules/space_control/components/WorkSpacesMap/types'
import { CARD_MODE } from 'modules/space_control/components/WorkSpacesMap/types'
import { WorkAreaMapLocationNode, useWorkAreaMap } from 'modules/space_control/hooks/useWorkAreaMap'
import { WorkSpaceNode, useWorkSpaces } from 'modules/space_control/hooks/useWorkSpaces'
import {
  SETTINGS_EVENT,
  SettingsEventType,
  useSettingsMapEvents,
} from 'modules/space_control/hooks/workSpacesMap/useSettingsMapEvents'

const WorkSpacesPage = () => {
  const apolloClient = useApolloClient()
  const { id, workAreaId } = useStrictParams<'id' | 'workAreaId'>()
  const [selectedWorkSpace, setSelectedWorkSpace] = useState<WorkSpaceNode | null>(null)
  const [hoveredWorkSpaceId, setHoveredWorkSpaceId] = useState<string | null>(null)
  const [showUploader, setShowUploader] = useState(false)

  const { data: permissionsData, loading: permissionsLoading } = useQuery(WorkSpacesPageDocument)

  const canEditSpace = permissionsData?.spaceControl._policies.workSpaceUpdate.can ?? false
  const canDeleteSpace = permissionsData?.spaceControl._policies.workSpaceDelete.can ?? false
  const canAddSpace = permissionsData?.spaceControl._policies.workSpaceCreate.can ?? false

  const mapMode = useMemo(
    () => (selectedWorkSpace ? MAP_MODE.PLACE_PIN : MAP_MODE.DEFAULT),
    [selectedWorkSpace]
  )

  // Provide stable date/time values for hover card queries
  const currentDate = useMemo(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    const day = String(now.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }, [])

  const { loading: mapLoading, workAreaMap } = useWorkAreaMapAvailability({
    workAreaId: workAreaId,
  })
  const { workAreaMapLocations, refetch: refetchWorkAreaMapLocations } = useWorkAreaMap({
    id: workAreaMap?.id || null,
    date: currentDate,
  })
  const {
    workSpaces: listWorkSpaces,
    controls: listWorkSpacesControls,
    query: listWorkSpacesQuery,
  } = useWorkSpaces({
    locationIds: [id],
    workAreaIds: [workAreaId],
    first: 50,
  })

  const handleSettingsEvent = useCallback(
    (event: SettingsEventType, payload?: { workSpaceLocation: WorkAreaMapLocationNode | null }) => {
      switch (event) {
        case SETTINGS_EVENT.PIN_PLACED:
          setSelectedWorkSpace(null)
          refetchWorkAreaMapLocations()
          break
        case SETTINGS_EVENT.PIN_DELETED:
          setSelectedWorkSpace(null)
          refetchWorkAreaMapLocations()
          break
        case SETTINGS_EVENT.PIN_HOVERED:
          const workSpace = payload?.workSpaceLocation?.workSpace || null
          setHoveredWorkSpaceId(workSpace?.id ?? null)
          break
      }
    },
    [refetchWorkAreaMapLocations]
  )

  const { actions, alertWorkSpace, handleDelete, isPlacingPin, isDeletingPin, dismissAlert } =
    useSettingsMapEvents({
      selectedWorkSpace,
      mapId: workAreaMap?.id,
      onEvent: handleSettingsEvent,
    })

  const sideContent = (
    <LocationWorkSpaces
      workAreaId={workAreaId}
      locationId={id}
      selectedWorkSpace={selectedWorkSpace}
      hoveredWorkSpaceId={hoveredWorkSpaceId}
      onWorkSpaceSelect={(ws) => {
        dismissAlert()
        setSelectedWorkSpace(ws)
      }}
      canEditSpace={canEditSpace}
      canDeleteSpace={canDeleteSpace}
      canAddSpace={canAddSpace}
      workSpaces={listWorkSpaces}
      onDeleteSuccess={() => {
        setSelectedWorkSpace(null)
      }}
      controls={listWorkSpacesControls}
    />
  )

  const mainContent = (
    <>
      {workAreaMap?.image?.url ? (
        <Box position="relative">
          <Box
            position="absolute"
            style={{
              top: '10%',
              left: '20%',
              right: '20%',
              zIndex: '9999',
              transform: 'translateY(-100%)',
              backgroundColor: 'white',
              borderRadius: '10px',
            }}
          >
            {alertWorkSpace && !isDeletingPin && (
              <PinAlert workSpace={alertWorkSpace} onDelete={handleDelete} />
            )}

            <AlertMessage
              message={
                isPlacingPin
                  ? i18n.t('space_control.map.placing_pin')
                  : isDeletingPin
                    ? i18n.t('space_control.map.deleting_pin')
                    : selectedWorkSpace
                      ? i18n.t('space_control.map.click_to_place_pin')
                      : undefined
              }
            />
          </Box>
          <WorkSpacesMap
            workAreaId={workAreaId}
            workAreaMapLocations={workAreaMapLocations}
            actions={actions}
            mapMode={mapMode}
            map={workAreaMap}
            cardMode={CARD_MODE.EDITING}
            date={currentDate}
          />
        </Box>
      ) : (
        <Box width="s400">
          <OneEmptyState
            title={i18n.t('space_control.overview.no_map_configured')}
            description=""
            emoji={'🗺️'}
            actions={[
              {
                label: i18n.t('settings.locations.location.work_areas.map.add_map'),
                onClick: () => setShowUploader(true),
                type: 'default',
                variant: 'outline',
              },
            ]}
          />
          {showUploader && (
            <FileUploaderModal
              workAreaId={workAreaId}
              onClose={() => setShowUploader(false)}
              onSuccess={() => {
                refetchWorkAreaMapLocations()
                setShowUploader(false)
              }}
            />
          )}
        </Box>
      )}
    </>
  )

  if (mapLoading || permissionsLoading) {
    return (
      <ResponsivePage.Body variant="full">
        <Loading />
      </ResponsivePage.Body>
    )
  }

  return (
    <>
      <ResponsivePage.Body variant="full">
        <Box minHeight="full">
          <WorkSpaceContext.Provider
            value={{
              onDeleted: async () => {
                refetchWorkAreaMapLocations()
                await apolloClient.refetchQueries({
                  include: [listWorkSpacesQuery.query],
                })
              },
            }}
          >
            <TwoColumnLayout sideContent={sideContent}>{mainContent}</TwoColumnLayout>
          </WorkSpaceContext.Provider>
        </Box>
      </ResponsivePage.Body>
      <Outlet />
    </>
  )
}

export default WorkSpacesPage
